import { ChangeEvent, FC, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import _ from '../../locale';
import { styled } from '../../theme';
import { INPUT_WIDTH, PAGINATION_CONTROL_HEIGHT } from './constants';

const pageControlStyles = (theme) => ({
    minWidth: INPUT_WIDTH,
    width: INPUT_WIDTH,
    height: PAGINATION_CONTROL_HEIGHT,
    border: 'none',
    borderRadius: theme.borderRadius.base,
    boxSizing: 'border-box',
    padding: '0 16px',
    background: theme.color.backgroundGray,
    color: theme.color.jetBlack,
    cursor: 'pointer',
    caretColor: theme.color.primary1,
    fontFamily: theme.typography.fontFamily,
    fontSize: '1.4rem',
    textAlign: 'center',
    transition: 'background-color 120ms ease, color 120ms ease',
    whiteSpace: 'nowrap',
    '&::placeholder': {
        color: theme.color.primary1,
        opacity: 1,
    },
    '&:hover, &:focus': {
        background: '#DDF6E9',
        color: theme.color.primary1,
    },
});

const PageDisplayButton = styled('button')(({ theme }) => ({
    ...pageControlStyles(theme),
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const PageInput = styled('input')(({ theme }) => pageControlStyles(theme));

type Props = {
    currentPage: number;
    pageCount: number;
    onChangePage: (page: number) => void;
};

const PageNumberControl: FC<Props> = ({ currentPage, pageCount, onChangePage }) => {
    const [isEditingPage, setIsEditingPage] = useState(false);
    const [isPageHovered, setIsPageHovered] = useState(false);
    const [pageInput, setPageInput] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const turnHoverOn = useCallback(() => setIsPageHovered(true), [setIsPageHovered]);
    const turnHoverOff = useCallback(() => {
        setIsPageHovered(false);
    }, [setIsPageHovered]);

    const turnEditOn = useCallback(() => setIsEditingPage(true), [setIsEditingPage]);
    const turnEditOff = useCallback(() => setIsEditingPage(false), [setIsEditingPage]);

    const stopEditing = useCallback(() => {
        turnEditOff();
        turnHoverOff();
        setPageInput('');
    }, [turnEditOff, turnHoverOff]);

    useEffect(() => {
        stopEditing();
    }, [currentPage, pageCount, stopEditing]);

    useEffect(() => {
        if (isEditingPage) {
            inputRef.current?.focus();
        }
    }, [isEditingPage]);

    const label = isPageHovered
        ? _('pagination_enter_page')
        : _('pagination_page_number_of_total', {
              page: currentPage,
              total: pageCount,
          });

    const commitPageInput = () => {
        const page = Number(pageInput);

        stopEditing();

        if (page > 0) {
            onChangePage(page);
        }
    };

    const handlePageInputChange = (ev: ChangeEvent<HTMLInputElement>) => {
        setPageInput(ev.target.value.replace(/\D/g, ''));
    };

    const handlePageInputKeyDown = (ev: KeyboardEvent<HTMLInputElement>) => {
        if (ev.key === 'Enter') {
            ev.preventDefault();
            commitPageInput();
        }

        if (ev.key === 'Escape') {
            ev.preventDefault();
            stopEditing();
        }
    };

    if (isEditingPage) {
        return (
            <PageInput
                aria-label={`Page number, 1 through ${pageCount}`}
                autoFocus
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={_('pagination_enter_page')}
                ref={inputRef}
                value={pageInput}
                onBlur={stopEditing}
                onChange={handlePageInputChange}
                onKeyDown={handlePageInputKeyDown}
            />
        );
    }

    return (
        <PageDisplayButton
            aria-label={`Current page, page ${currentPage} of ${pageCount}. Edit page number`}
            onBlur={turnHoverOff}
            onClick={turnEditOn}
            onFocus={turnHoverOn}
            onMouseEnter={turnHoverOn}
            onMouseLeave={turnHoverOff}
            type="button"
        >
            {label}
        </PageDisplayButton>
    );
};

export default PageNumberControl;
